import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Pencil, Trash2, Plus } from "lucide-react";

interface SEOText {
  id: string;
  section_key: string;
  title: string;
  content: string;
  sort_order: number;
  is_active: boolean;
}

const emptySEO = { section_key: "", title: "", content: "", sort_order: 0, is_active: true };

export default function LandingSEO() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<SEOText> | null>(null);
  const [open, setOpen] = useState(false);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["landing_seo_texts"],
    queryFn: async () => {
      const { data, error } = await supabase.from("landing_seo_texts").select("*").order("sort_order");
      if (error) throw error;
      return data as SEOText[];
    },
  });

  const save = useMutation({
    mutationFn: async (r: Partial<SEOText>) => {
      if (r.id) {
        const { error } = await supabase.from("landing_seo_texts").update(r).eq("id", r.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("landing_seo_texts").insert([r as any]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["landing_seo_texts"] });
      setOpen(false);
      toast.success("Сохранено");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("landing_seo_texts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["landing_seo_texts"] });
      toast.success("Удалено");
    },
  });

  return (
    <AdminLayout title="SEO-тексты лендинга">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground text-sm">Текстовые блоки для SEO-оптимизации главной страницы</p>
        <Button onClick={() => { setEditing({ ...emptySEO }); setOpen(true); }} size="sm"><Plus className="w-4 h-4 mr-1" />Добавить</Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead className="w-32">Ключ</TableHead>
              <TableHead>Заголовок</TableHead>
              <TableHead>Активен</TableHead>
              <TableHead className="w-24">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Загрузка...</TableCell></TableRow>
            ) : items.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.sort_order}</TableCell>
                <TableCell className="font-mono text-xs">{r.section_key}</TableCell>
                <TableCell className="font-medium">{r.title}</TableCell>
                <TableCell>{r.is_active ? "✅" : "❌"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing({ ...r }); setOpen(true); }}><Pencil className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => { if(confirm("Удалить?")) remove.mutate(r.id); }}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Редактировать текст" : "Новый текст"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1"><Label>Ключ секции</Label><Input value={editing?.section_key || ""} onChange={e => setEditing(p => ({...p!, section_key: e.target.value}))} placeholder="intro, events, how..." /></div>
              <div className="w-24"><Label>Порядок</Label><Input type="number" value={editing?.sort_order || 0} onChange={e => setEditing(p => ({...p!, sort_order: +e.target.value}))} /></div>
            </div>
            <div><Label>Заголовок</Label><Input value={editing?.title || ""} onChange={e => setEditing(p => ({...p!, title: e.target.value}))} /></div>
            <div><Label>Содержимое (поддерживает абзацы через пустую строку)</Label><Textarea rows={10} value={editing?.content || ""} onChange={e => setEditing(p => ({...p!, content: e.target.value}))} /></div>
            <div className="flex items-center gap-2">
              <Switch checked={editing?.is_active ?? true} onCheckedChange={v => setEditing(p => ({...p!, is_active: v}))} />
              <Label>Активен</Label>
            </div>
            <Button className="w-full" onClick={() => editing && save.mutate(editing)} disabled={save.isPending}>
              {save.isPending ? "Сохранение..." : "Сохранить"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
